import React from "react";
import { withRouter } from "react-router-dom";
import clsx from 'clsx';
import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress } from '@material-ui/core';

import withCurrentUser from "../../../hocs/with_current_user.js";
import withShowToast from '../../../hocs/with_show_toast.js';

import WebRoutes from "../../../web/routes.js";

import I18n from "../../../base/i18n.js";

import Container from "../../../base/components/container.js";
import { ButtonVariantOutlined } from "../../../base/buttons/constants.js";
import Button from "../../../base/buttons/button.js";

import IcEdit from "../../../resources/icons/ic_edit.js";

import { ProjectModel } from "../../../models/projects/project_model.js";

import styles from "./styles.js";

import PersonalInfoFields from "../../../base/user_fields/personal_info_fields.js";
import QuestionnaireFields from "../../../base/user_fields/questionnaire_fields.js";

import PaymentMethodSection from "./components/payment_method_section.js";
import ConfirmationEmailSection from "./components/confirmation_email_section.js";
import InvestmentConfirmationSection from "./components/investment_confirmation_section.js"

import ShowProjectUseCase from "../../../use_cases/projects/projects/show_project_use_case.js";
import NewInvestmentUseCase from "../../../use_cases/projects/investments/new_investment_use_case.js";
import ShowLimitInvestmentUseCase from "../../../use_cases/projects/investments/show_limit_investments_use_case.js";

import InvestmentPersonalInfoUseCase from "../../../use_cases/projects/investment_steps/investment_personal_info_use_case.js";
import InvestmentQuestionnaireUseCase from "../../../use_cases/projects/investment_steps/investment_questionnaire_use_case.js";
import InvestmentPaymentMethodUseCase from "../../../use_cases/projects/investment_steps/investment_payment_method_use_case.js";
import InvestmentConfirmationUseCase from "../../../use_cases/projects/investment_steps/investment_confirmation_use_case.js"

import Analytics from "../../../utils/analytics";

import { NotFoundErrorCode } from "../../../constants.js";

const PersonalInfoStep = "personal_info";
const QuestionnaireStep = "questionnaire";
const PaymentMethodStep = "payment_method";
const ConfirmationEmailStep = "information_emailed"
const InvestmentConfirmationStep = "confirmation"

const ScrollToSectionDelta = 54;

class Invest extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      prevState: undefined,
      step: undefined,
      allAccepted: false,
      formLoaded: false,
      limitForInvestmentAmount: "",
      isFirstLoad: true,
      confirmationInfo: [],
      title: props.currentUser.personalInfo.title
    };

    this.nominalInvestmentAmount = 100;
    this.minInvestmentAmount     = 100.00;
    this.investmentAmountStepsMinCount = 1;
    this.investmentAmountStepsCount = 0;

    this.investment = {};

    this.sections = {
      [PersonalInfoStep]: {
        title: I18n.t("web.modal.submit_investment.personal_info.title"),
        content: this.personalInfoContent
      },
      [QuestionnaireStep]: {
        title: I18n.t("web.modal.submit_investment.questionnaire.title"),
        content: this.questionnaireContent
      },
      [PaymentMethodStep]: {
        title: I18n.t("web.modal.submit_investment.payment_method.title"),
        content: this.paymentMethodContent
      },
      [ConfirmationEmailStep]: {
        title: I18n.t("web.page.investment.confirmation_emailed.title"),
        content: this.confirmationEmailContent
      },
      [InvestmentConfirmationStep]: {
        title: I18n.t("web.modal.submit_investment.confirmation.title"),
        content: this.investmentConfirmationContent
      }
    }

    this.passedSteps = {
      PersonalInfoStep: false,
      QuestionnaireStep: false,
      PaymentMethodStep: false,
      ConfirmationEmailStep: false,
      InvestmentConfirmationStep: false
    }

    this.sectionAnchorsRefs = {

    }
  }

  componentDidMount() {
    const { slug } = this.props;
    if (!slug) {
      return;
    }
    ShowProjectUseCase.execute(slug)
      .then((result) => {
        const { data: { project, nominalInvestmentAmount: nominal } } = result;
        this.project = project
        this.nominalInvestmentAmount = parseInt(nominal) || 100;
        this.minInvestmentAmount = parseFloat(this.project.minInvestmentAmount || this.nominalInvestmentAmount)
        this.investmentAmountStepsMinCount = Math.ceil(this.minInvestmentAmount / this.nominalInvestmentAmount);

        NewInvestmentUseCase.execute(project.id)
          .then((result) => {
            const { data: { investment, allowInlineProfileEditing, confirmationInfo } } = result;
            this.updateFormData(investment);

            this.investment = investment

            let step = this.investment.status
            const { amount } = investment;
            this.setState({
              allowInlineProfileEditing,
              confirmationInfo,
              amount,
              step
            })
            this.fetchAdditionalInfo()
          })
          .catch((result) => {
            this.setState({
              formLoaded: true
            })
            this.props.showServerErrorIfNeed(result);
          })
      })
      .catch((result) => {
        const { history } = this.props;
        const { errorCode } = result;
        if (errorCode === NotFoundErrorCode) {
          history.replace(WebRoutes.pageNotFound());
          return;
        }
        this.props.showServerErrorIfNeed(result);
      })
  }

  componentDidUpdate(_, prevState) {
    if (this.state.errorData && !prevState.errorData) {
      const { step, errorData } = this.state;
      const { fields: { amount } = {},
        paymentMethod,
        participant: {
          user: { personalInfo, questionnaire } = {},
        } = {}
      } = errorData || {};
      let stepToScroll;
      if (personalInfo) {
        stepToScroll = PersonalInfoStep
      }
      else if (questionnaire) {
        stepToScroll = QuestionnaireStep;
      }
      else if (paymentMethod || amount) {
        stepToScroll = PaymentMethodStep;
      }

      if (stepToScroll) {
        if (step !== stepToScroll) {
          this.onNextStep(stepToScroll);
        }
      }
    }
    if (prevState.step !== this.state.step) {
      this.trackStepChange();
    }
    if (this.state.isFirstLoad && this.state.formLoaded) {
      this.scrollToSection(this.state.step);
      this.setState({
        isFirstLoad: false
      })
    }
  }

  fetchAdditionalInfo = () => {
    ShowLimitInvestmentUseCase.execute(this.investment)
      .then(result => {
        this.setState({
          formLoaded: true,
          limitForInvestmentAmount: parseFloat(result.data.limitForInvestment)
        })
      })
      .catch((result) => {
        this.setState({
          formLoaded: true
        })
        this.props.showErrorMessage(result);
      })
  }

  // Personal info
  personalInfoContent = () => {
    const { classes } = this.props;

    const { submit, allowInlineProfileEditing, errorData } = this.state;
    const { participant: { user: { personalInfo: personalInfoErrors } = {} } = {} } = errorData || {};

    const { participant: { user: { personalInfo = {} } = {} } } = this.investment;

    return <div className={classes.personalInfo} >
      {
        !allowInlineProfileEditing &&
        <div className={classes.editProfileRow} >
          <Button className={classes.editProfile}
            variant={ButtonVariantOutlined}
            onClick={this.onEditProfile} >
            <IcEdit className={classes.editProfileIcon} />
            <span>
              {I18n.t("action.edit")}
            </span>
          </Button>
        </div>
      }
      <PersonalInfoFields item={personalInfo}
        onChange={this.onProfileChanged}
        readOnly={!allowInlineProfileEditing}
        errorData={personalInfoErrors}
        scrollToElement={this.scrollToElement} />
      <div className={classes.buttonContainer} >
        <Button disabled={submit}
          type={"submit"}
          className={classes.button} >
          {I18n.t("action.next")}
        </Button>
      </div>
    </div>
  }

  onEditProfile = () => {
    this.setState({
      allowInlineProfileEditing: true
    })
  }

  onProfileChanged = (personalInfo) => {
    this.investment.participant.user.personalInfo = personalInfo;
    this.passedSteps[PersonalInfoStep] = false;
    this.passedSteps[QuestionnaireStep] = false;
    this.passedSteps[PaymentMethodStep] = false;

    this.currentStep = PersonalInfoStep;
  }

  // Questionnaire
  questionnaireContent = () => {
    const { classes } = this.props;

    const { submit, errorData } = this.state;
    const { participant: { user: { questionnaire: questionnaireErrors } = {} } = {} } = errorData || {};

    const { participant: { user: { questionnaire: { data: questions } } } } = this.investment;

    return <div className={clsx({
      [classes.questionnaire]: true,
    })}>
      <QuestionnaireFields onChange={this.onQuestionnaireChanged}
        questions={questions}
        showDisclaimer={true}
        errorData={questionnaireErrors} />
      <div className={classes.buttonContainer} >
        <Button disabled={submit}
          className={classes.button}
          type={"submit"} >
          {I18n.t("action.next")}
        </Button>
      </div>
    </div>
  }

  onQuestionnaireChanged = (questions) => {
    this.investment.participant.user.questionnaire.data = questions;
    this.passedSteps[QuestionnaireStep] = false;
    this.passedSteps[PaymentMethodStep] = false;

    this.currentStep = QuestionnaireStep;
    this.fetchAdditionalInfo()
  }

  // Payment method
  paymentMethodContent = () => {
    const { submit, limitForInvestmentAmount, amount, title } = this.state;
    const { errorData } = this.state;
    const {
      paymentMethod: paymentMethodErrors,
      fields: { amount: amountErrors } = {}
    } = errorData || {};
    const { classes } = this.props;

    return <div className={clsx({
      [classes.bankData]: true,
    })}>
      <PaymentMethodSection
        stepsCount={this.investmentAmountStepsCount}
        amount={amount}
        title={title}
        paymentMethod={this.investment.paymentMethod}
        nominalInvestmentAmount={this.nominalInvestmentAmount}
        minInvestmentAmount={this.minInvestmentAmount}
        limitForInvestmentAmount={limitForInvestmentAmount}
        onChangeStepsCount={this.onChangeStepsCount}
        onChangePaymentMethod={this.onChangePaymentMethod}
        onChangeStepsBonuscode={this.onChangeStepsBonuscode}
        amountErrors={amountErrors}
        paymentMethodErrors={paymentMethodErrors} />
      <div className={classes.buttonContainer} >
        <Button disabled={submit}
          type={"submit"}
          className={classes.button} >
          {I18n.t("action.send")}
        </Button>
      </div>
    </div>
  }

  confirmationEmailContent = () =>  <ConfirmationEmailSection project={this.project} />


  investmentConfirmationContent = () => {
    const { classes } = this.props;
    const { submit, errorData, confirmationInfo } = this.state;
    const { signature: signatureErrors } = errorData || {};

    return <div>
      <InvestmentConfirmationSection
        project={this.project}
        investment={this.investment}
        signature={this.investment.signature}
        confirmationInfo={confirmationInfo}
        onChangeConfirmationInfo={this.onChangeConfirmationInfo}
        onChangeSignature={this.onChangeSignature}
        signatureErrors={signatureErrors}
      />
      <div className={classes.buttonContainer} >
        <Button disabled={submit || !this.isAllChecked()}
          type={"submit"}
          className={classes.button} >
          {I18n.t("action.invest_now")}
        </Button>
      </div>
    </div>
  }

  onChangeConfirmationInfo = (newConfirmationInfo) => {
    this.setState({
      confirmationInfo: newConfirmationInfo
    })
  }

  onChangeSignature = (newSignature) => {
    this.investment.signature = newSignature;
  }

  isAllChecked = () => {
    const { confirmationInfo } = this.state;
    return (confirmationInfo.filter(info => info.type === "checkbox" && info.isRequired) || [])
      .map(info => info.value || false)
      .reduce((total, current) => total && current);
  }

  onChangeStepsCount = (count) => {
    this.investmentAmountStepsCount = count;
    this.investment.amount = this.nominalInvestmentAmount * this.investmentAmountStepsCount;
    this.setState({
      amount: this.investment.amount
    })

    this.fetchAdditionalInfo();

    this.passedSteps[PaymentMethodStep] = false;

    this.currentStep = PaymentMethodStep;
  }

  onChangePaymentMethod = (paymentMethod) => {
    this.investment.paymentMethod = paymentMethod;
    this.passedSteps[PaymentMethodStep] = false;

    this.currentStep = PaymentMethodStep;
  }

  onChangeStepsBonuscode = (bonuscode) => {
    this.investment.bonuscode = bonuscode;
    this.passedSteps[PaymentMethodStep] = false;

    this.currentStep = PaymentMethodStep;
  }

  onNextStep = (nextStep) => {
    const { step } = this.state;
    if (step === nextStep) {
      this.setState({
        prevStep: step,
        step: undefined
      });
      return;
    }
    if (this.currentStep !== nextStep && !this.passedSteps[nextStep]) {

      if (!step) {
        this.setState({
          prevStep: undefined,
          step: this.currentStep
        });
        this.scrollToSection(this.currentStep);
      }
      this.props.showWarningMessage(I18n.t("web.modal.submit_investment.next_step_alert"));
      return;
    }
    this.setState({
      prevStep: step,
      step: nextStep
    });
    this.scrollToSection(nextStep);
  }

  scrollToElement = (element) => {
    this.elementToScroll = element;
    this.scrollToField();
  }

  scrollToSection = (sectionId) => {
    if(this.elementToScroll) {
      return;
    }

    const section = this.sectionAnchorsRefs[sectionId];
    window.scroll(0, this.accordionContainer.offsetTop + section.offsetTop + ScrollToSectionDelta)
  }

  scrollToField = () => {
    if (!this.elementToScroll) {
      return;
    }
    window.scroll(0, this.elementToScroll.offsetTop)
    this.elementToScroll = undefined;
  }

  updateFormData = (investment) => {
    this.investment = {
      ...investment
    }

    this.fillPassedSteps();
    this.fillAmountIfNeed();
  }

  fillPassedSteps = () => {
    const { status } = this.investment;
    this.currentStep = status;
    if (status === QuestionnaireStep) {
      this.passedSteps[PersonalInfoStep] = true;
    }
    if (status === PaymentMethodStep) {
      this.passedSteps[PersonalInfoStep] = true;
      this.passedSteps[QuestionnaireStep] = true;
    }
    if (status === ConfirmationEmailStep) {
      this.passedSteps[PersonalInfoStep] = true;
      this.passedSteps[QuestionnaireStep] = true;
      this.passedSteps[PaymentMethodStep] = true;
    }
    if (status === InvestmentConfirmationStep) {
      this.passedSteps[PersonalInfoStep] = true;
      this.passedSteps[QuestionnaireStep] = true;
      this.passedSteps[PaymentMethodStep] = true;
      this.passedSteps[ConfirmationEmailStep] = true;
    }
  }

  fillAmountIfNeed = () => {
    if (this.investment.amount && this.investment.amount) {
    //   this.investment.amount = this.nominalInvestmentAmount * this.investmentAmountStepsMinCount;
    //   this.investmentAmountStepsCount = this.investmentAmountStepsMinCount;
    // }
    // else {
      this.investmentAmountStepsCount = this.investment.amount / this.nominalInvestmentAmount;
    }
  }

  onSubmit = (event) => {
    event.preventDefault();

    const { step } = this.state;

    let useCase;
    if (step === PersonalInfoStep) {
      useCase = InvestmentPersonalInfoUseCase.execute(this.investment);
    }
    else if (step === QuestionnaireStep) {
      useCase = InvestmentQuestionnaireUseCase.execute(this.investment);
    }
    else if (step === PaymentMethodStep || step == ConfirmationEmailStep) {
      useCase = InvestmentPaymentMethodUseCase.execute(this.investment);
    } else if (step === InvestmentConfirmationStep) {
      useCase = InvestmentConfirmationUseCase.execute(this.investment);
    }

    this.setState({
      submit: true,
      errorData: undefined
    })

    useCase.then((result) => {
      const { data: { investment } } = result;
      this.updateFormData(investment);

      this.setState({
        submit: false
      });

      const { status: nextStep } = this.investment;
      if (step == InvestmentConfirmationStep) {

        this.investment = {
          ...investment
        }

        this.setState({
          submit: false
        });

        const { onSave, history } = this.props;
        if (onSave) {
          onSave(investment);
        }
        Analytics.investmentSuccessfullyCreated(this.investment);
        history.replace(WebRoutes.investment(investment));
      } else {
        this.currentStep = nextStep;

        this.onNextStep(nextStep);
      }
    })
      .catch((result) => {
        const { errorData, message } = result;
        const {
          fields: { amount: amountErrors } = {}
        } = errorData || {};

        this.setState({
          errorData: errorData,
          submit: false
        })
        this.props.showServerErrorIfNeed(message);
        if(amountErrors) {
          this.scrollToSection(this.state.step);
        }
      })
  }

  trackStepChange = () => {
    const { step } = this.state;
    if (!step) {
      return;
    }
    if (step === PersonalInfoStep) {
      Analytics.investmentAtPersonalInfoStep(this.investment);
    }
    if (step === QuestionnaireStep) {
      Analytics.investmentAtQuestionnaireStep(this.investment);
    }
    if (step === PaymentMethodStep) {
      Analytics.investmentAtPaymentMethodStep(this.investment);
    }
  }

  render() {
    const { classes } = this.props;

    const { step, formLoaded } = this.state;

    if (!formLoaded) return <CircularProgress className={classes.loader} />

    // switching between waiting for email confirmation and investment confirmation
    const keys = Object.keys(this.sections).filter((key) => (this.investment.status == InvestmentConfirmationStep && key != ConfirmationEmailStep) || (this.investment.status != InvestmentConfirmationStep && key != InvestmentConfirmationStep) )

    return <div className={ classes.root } >
      <Container className = { classes.container } >
        <form onSubmit={this.onSubmit} >
          <h1 className={classes.intro} >
            {I18n.t("web.modal.submit_investment.intro", { project: ProjectModel.displayValue(this.project) })}
          </h1>
          <div className={classes.accordionContainer} ref={ref => this.accordionContainer = ref} >
            <div className={classes.accordionAnchors} >
              {
                keys.map((key, index) => {
                  const section = this.sections[key];
                  return <Accordion expanded={false}
                    className={classes.section}
                    key={key}
                    ref={ref => this.sectionAnchorsRefs[key] = ref} >
                    <AccordionSummary className={classes.sectionTitle} >
                      <div className={classes.sectionTitleContent}>
                        <div className={clsx({
                          [classes.sectionNumber]: true,
                          [classes.current]: step === key
                        })} >
                          {index + 1}
                        </div>
                        <div className={classes.sectionName} >
                          {section.title}
                        </div>
                      </div>
                    </AccordionSummary>
                  </Accordion>
                })
              }
            </div>
            <div className={classes.accordion} >
              {
                formLoaded &&
                keys.map((key, index) => {
                  const section = this.sections[key];
                  return <Accordion expanded={step === key}
                    onChange={() => this.onNextStep(key)}
                    className={clsx({
                      [classes.section]: true
                    })}
                    key={key}
                    TransitionProps={{
                      onEntered: () => {
                        this.scrollToField();
                      },
                      timeout: this.elementToScroll ? 0 : "auto"
                    }} >
                    <AccordionSummary className={classes.sectionTitle} >
                      <div className={classes.sectionTitleContent}>
                        <div className={clsx({
                          [classes.sectionNumber]: true,
                          [classes.current]: step === key
                        })} >
                          {index + 1}
                        </div>
                        <div className={classes.sectionName} >
                          {section.title}
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className={classes.sectionContent} >
                      {section.content()}
                    </AccordionDetails>
                  </Accordion>
                })
              }
            </div>
          </div>
        </form>
      </Container>
    </div>
  }
}

export default compose(
  withRouter,
  withShowToast,
  withCurrentUser,
  withStyles(styles)
)(Invest);
